"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ThumbsUp, ThumbsDown } from "lucide-react"

/**
 * Viewport-relative position coordinates for the floating popover.
 */
interface PopoverPosition {
  top: number
  left: number
}

interface HighlightFeedbackPopoverProps {
  /**
   * Ref to the container element whose text can be highlighted.
   * Used to verify the selection is within this message.
   */
  containerRef: React.RefObject<HTMLDivElement | null>
  /**
   * Whether selection-based feedback is disabled (e.g. while streaming).
   */
  disabled?: boolean
  /**
   * Callback fired when the user clicks a feedback button on highlighted text.
   *
   * @param feedbackType - "positive" or "negative"
   * @param highlightedText - The exact text the user selected.
   */
  onHighlightFeedback: (
    feedbackType: "positive" | "negative",
    highlightedText: string
  ) => void
}

/**
 * HighlightFeedbackPopover
 *
 * A floating toolbar rendered in a React portal that appears when the user
 * highlights (selects) text within an assistant message. Provides
 * thumbs-up/thumbs-down buttons for targeted feedback on a specific passage.
 *
 * Dismissal: The popover disappears when the selection is cleared (click
 * elsewhere, or Escape key).
 */
export function HighlightFeedbackPopover({
  containerRef,
  disabled = false,
  onHighlightFeedback,
}: HighlightFeedbackPopoverProps): React.JSX.Element | null {
  const [visible, setVisible] = useState<boolean>(false)
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 })
  const [selectedText, setSelectedText] = useState<string>("")
  const popoverRef = useRef<HTMLDivElement>(null)

  /**
   * Determines whether the current window selection is fully contained
   * within the message container and is non-empty.
   *
   * @returns The selected text and the position of the selection start,
   *          or null if the selection is empty or extends outside the container.
   */
  const getValidSelection = useCallback((): {
    text: string
    startRect: DOMRect
  } | null => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || !containerRef.current) {
      return null
    }

    const text = selection.toString().trim()
    if (!text) {
      return null
    }

    // Ensure the selection is fully within our container
    const range = selection.getRangeAt(0)
    if (!containerRef.current.contains(range.commonAncestorContainer)) {
      return null
    }

    // Get the position where the selection actually starts (first character)
    // by creating a collapsed range at the start offset.
    const startRange = document.createRange()
    startRange.setStart(range.startContainer, range.startOffset)
    startRange.setEnd(range.startContainer, range.startOffset)
    const startRect = startRange.getBoundingClientRect()
    return { text, startRect }
  }, [containerRef])

  /**
   * Handles pointerup events on the document. If the user just finished
   * selecting text within the container, show the popover.
   */
  const handlePointerUp = useCallback(() => {
    if (disabled) return

    // Small delay to let the browser finalize the selection
    requestAnimationFrame(() => {
      const result = getValidSelection()
      if (!result) {
        setVisible(false)
        return
      }

      // Position above and slightly to the left of where the selection
      // starts.
      const POPOVER_HEIGHT = 28
      const GAP = 6
      const NUDGE_LEFT = 4 // slight leftward offset for visual alignment
      const top = result.startRect.top - POPOVER_HEIGHT - GAP
      const left = result.startRect.left - NUDGE_LEFT

      setPosition({
        top: Math.max(GAP, top),
        left: Math.max(GAP, left),
      })
      setSelectedText(result.text)
      setVisible(true)
    })
  }, [disabled, getValidSelection])

  /**
   * Dismiss the popover when the selection changes to empty (e.g. user
   * clicks elsewhere).
   */
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) {
      // Delay slightly so clicking the popover buttons doesn't
      // dismiss before the click registers.
      setTimeout(() => {
        const currentSelection = window.getSelection()
        if (!currentSelection || currentSelection.isCollapsed) {
          setVisible(false)
        }
      }, 150)
    }
  }, [])

  /**
   * Dismiss on Escape key press.
   */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setVisible(false)
      window.getSelection()?.removeAllRanges()
    }
  }, [])

  useEffect(() => {
    document.addEventListener("pointerup", handlePointerUp)
    document.addEventListener("selectionchange", handleSelectionChange)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("pointerup", handlePointerUp)
      document.removeEventListener("selectionchange", handleSelectionChange)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [handlePointerUp, handleSelectionChange, handleKeyDown])

  /**
   * Handle feedback button click. Captures the highlighted text and
   * passes it up, then dismisses the popover.
   */
  const handleFeedbackClick = (type: "positive" | "negative") => {
    if (!selectedText) return
    onHighlightFeedback(type, selectedText)
    setVisible(false)
    window.getSelection()?.removeAllRanges()
  }

  if (!visible) return null

  return createPortal(
    <div
      ref={popoverRef}
      role="toolbar"
      aria-label="Feedback on selected text"
      className="fixed z-[9999] flex items-center gap-0.5 rounded-full border border-border bg-popover px-1.5 py-0.5 shadow-md animate-in fade-in-0 zoom-in-95 duration-100"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      // Prevent the popover from stealing focus/selection when clicked
      onPointerDown={(e) => e.preventDefault()}
    >
      <button
        onClick={() => handleFeedbackClick("positive")}
        className="p-1 text-muted-foreground hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
        aria-label="Positive feedback on selected text"
      >
        <ThumbsUp size={12} />
      </button>
      <button
        onClick={() => handleFeedbackClick("negative")}
        className="p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
        aria-label="Negative feedback on selected text"
      >
        <ThumbsDown size={12} />
      </button>
    </div>,
    document.body
  )
}
